// frontend/util/audio-primer.mjs
/**
 * Ultra-simple Safari/iOS audio primer
 * Just creates one AudioContext on user gesture to unlock audio for the session
 */
export default class AudioPrimer {
    static #unlocked = false;

    /**
     * Check if this browser needs audio unlocking
     */
    static needsUnlocking() {
        const userAgent = navigator.userAgent;
        return (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) || 
               /iPhone|iPad|iPod/.test(userAgent);
    }

    /**
     * Unlock audio for the entire session - call on user gesture
     */
    static unlock() {
        if (this.#unlocked) return true;

        if (!this.needsUnlocking()) {
            this.#unlocked = true;
            return true;
        }

        try {
            console.log('Unlocking Safari audio...');

            // Create AudioContext and immediately discard it
            // This grants permission for future AudioContext creation
            const tempContext = new (window.AudioContext || window.webkitAudioContext)();

            // Resume if suspended (though it usually isn't on first creation)
            if (tempContext.state === 'suspended') {
                tempContext.resume().then(() => {
                    console.log('Safari audio context resumed');
                }).catch(err => {
                    console.warn('Failed to resume audio context:', err);
                });
            }

            // Create a silent audio buffer and play it to fully unlock audio
            const buffer = tempContext.createBuffer(1, 1, 22050);
            const source = tempContext.createBufferSource();
            source.buffer = buffer;
            source.connect(tempContext.destination);
            source.start();

            // We don't even need to keep this context - just creating it unlocks audio
            console.log('Safari audio unlocked!');
            this.#unlocked = true;
            return true;

        } catch (error) {
            console.error('Failed to unlock Safari audio:', error);
            return false;
        }
    }

    /**
     * Check if audio is unlocked
     */
    static get isUnlocked() {
        return this.#unlocked || !this.needsUnlocking();
    }
}
